import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import {
   AfterViewInit,
   Component,
   ElementRef,
   Input,
   OnChanges
} from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import { DateTime } from 'luxon';
import { ChartModule } from 'primeng/chart';
import { DashboardRiskTrendPointResponse } from '../../../services/response/user-property-dashboard-response';
import { DashboardCardComponent } from '../dashboard-card/dashboard-card.component';

@Component({
   selector: 'app-dashboard-risk-trend',
   standalone: true,
   imports: [CommonModule, ChartModule, DashboardCardComponent, DialogModule],
   templateUrl: './risk-trend.component.html',
   styleUrls: ['./risk-trend.component.scss']
})
export class RiskTrendComponent implements OnChanges, AfterViewInit {
   @Input() trend: DashboardRiskTrendPointResponse[] | null = null;
   @Input() loading = false;
   @Input() error = false;
   expanded = false;

   chartData: ChartData<'bar'> = {
      labels: [],
      datasets: []
   };

   chartOptions: ChartOptions<'bar'> = {};

   private viewReady = false;

   constructor(private elementRef: ElementRef<HTMLElement>) {}

   ngAfterViewInit(): void {
      this.viewReady = true;
      this.buildChart();
   }
   ngOnChanges(): void {
      if (this.viewReady) {
         this.buildChart();
      }
   }

   private buildChart(): void {
      if (!this.trend?.length) {
         this.chartData = {
            labels: [],
            datasets: []
         };

         return;
      }

      const styles = getComputedStyle(this.elementRef.nativeElement);

      const financialColour = this.getCssVariable(
         styles,
         '--dashboard-chart-financial',
         '#5751a9'
      );

      const financialFill = this.getCssVariable(
         styles,
         '--dashboard-chart-financial-fill',
         'rgba(87, 81, 169, 0.34)'
      );

      const operationsColour = this.getCssVariable(
         styles,
         '--dashboard-chart-operations',
         '#278b83'
      );

      const operationsFill = this.getCssVariable(
         styles,
         '--dashboard-chart-operations-fill',
         'rgba(39, 139, 131, 0.28)'
      );

      const textColour = this.getCssVariable(
         styles,
         '--text-color-secondary',
         '#6b7280'
      );

      const gridColour = this.getCssVariable(
         styles,
         '--surface-border',
         'rgba(0, 0, 0, 0.08)'
      );

      const labels = this.trend.map((point) =>
         DateTime.fromISO(point.businessDate).toFormat('dd MMM')
      );

      const financialRiskValues: number[] = this.trend.map(
         (point) => point.financialRiskValue ?? 0
      );

      const operationsRiskValues: number[] = this.trend.map(
         (point) => point.operationsRiskCount ?? 0
      );

      this.chartData = {
         labels,
         datasets: [
            {
               label: 'Financial Risk',
               data: financialRiskValues,
               yAxisID: 'y',
               backgroundColor: financialFill,
               borderColor: financialColour,
               borderWidth: 1,
               maxBarThickness: 36
            },
            {
               label: 'Operations Risk',
               data: operationsRiskValues,
               yAxisID: 'y1',
               backgroundColor: operationsFill,
               borderColor: operationsColour,
               borderWidth: 1,
               maxBarThickness: 36
            }
         ]
      };

      this.chartOptions = {
         responsive: true,
         maintainAspectRatio: false,
         animation: {
            duration: 250
         },
         interaction: {
            mode: 'index',
            intersect: false
         },
         plugins: {
            datalabels: {
               display: false
            },
            legend: {
               position: 'bottom',
               labels: {
                  color: textColour,
                  usePointStyle: true,
                  pointStyle: 'circle',
                  padding: 18,
                  boxWidth: 8,
                  boxHeight: 8
               }
            },
            tooltip: {
               callbacks: {
                  label: (context) => {
                     const value = context.parsed.y ?? 0;

                     if (context.datasetIndex === 0) {
                        return `Financial Risk: ${this.formatCurrency(value)}`;
                     }

                     return `Operations Risk: ${value}`;
                  }
               }
            }
         },
         scales: {
            x: {
               grid: {
                  display: false
               },
               ticks: {
                  color: textColour
               }
            },
            y: {
               beginAtZero: true,
               position: 'left',
               title: {
                  display: true,
                  text: 'Financial Risk',
                  color: textColour
               },
               ticks: {
                  color: textColour
               },
               grid: {
                  color: gridColour
               }
            },
            y1: {
               beginAtZero: true,
               position: 'right',
               title: {
                  display: true,
                  text: 'Operations Risk',
                  color: textColour
               },
               ticks: {
                  color: textColour,
                  precision: 0
               },
               grid: {
                  drawOnChartArea: false
               }
            }
         }
      };
   }

   private getCssVariable(
      styles: CSSStyleDeclaration,
      variableName: string,
      fallback: string
   ): string {
      const value = styles.getPropertyValue(variableName).trim();

      return value || fallback;
   }

   private formatCurrency(value: number): string {
      return new Intl.NumberFormat('en-AU', {
         style: 'currency',
         currency: 'AUD',
         minimumFractionDigits: 2,
         maximumFractionDigits: 2
      }).format(value);
   }

   openExpandedChart(): void {
      if (this.loading || this.error || !this.trend?.length) {
         return;
      }

      this.expanded = true;
   }

   closeExpandedChart(): void {
      this.expanded = false;
   }
}
